import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Filter } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ["Honeymoon", "Adventure", "Family", "Luxury", "Budget", "Group"];

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    setFilteredPackages(packages.filter((pkg) => pkg.category === selectedCategory));
  }, [selectedCategory, packages]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
      setFilteredPackages(response.data.filter((pkg) => pkg.category === categories[0]));
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="packages-page" className="grain-texture min-h-screen">
      <SEO
        title="Andaman Tour Packages - Honeymoon, Family, Adventure & Luxury Trips"
        description="Browse curated Andaman tour packages starting at ₹15,999. Honeymoon, family, adventure and luxury trips to Port Blair, Havelock and Neil Island with online booking."
        path="/packages"
      />
      <Navbar />

      {/* Hero Header */}
      <section className="pt-32 pb-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="accent-label text-secondary">TOUR PACKAGES</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mt-4 mb-6 text-white">
            Explore Our Packages
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Choose from our carefully curated collection of tours designed to showcase the best of Andaman.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-muted sticky top-20 z-40 border-b border-border" data-testid="filter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filter by Category:</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`filter-${category.toLowerCase()}`}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? "default" : "outline"}
                className={selectedCategory === category ? "bg-primary" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 bg-background" data-testid="packages-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">No packages found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPackages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  data-testid={`package-card-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="package-card bg-white rounded-lg overflow-hidden border border-border/50 shadow-sm"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={pkg.image_url}
                      alt={pkg.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-semibold uppercase">
                      {pkg.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-serif mb-2">{pkg.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{pkg.duration}</p>
                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {pkg.description}
                    </p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-2xl font-bold text-primary">
                        ₹{pkg.price.toLocaleString('en-IN')}
                      </span>
                      <Link
                        to={`/packages/${pkg.id}`}
                        data-testid={`view-details-${index}`}
                        className="text-secondary hover:text-secondary/80 font-medium flex items-center"
                      >
                        View Details <ArrowRight className="ml-1" size={16} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Packages;